"use client";

import {useEffect, useMemo, useState} from "react";
import Autoplay from "embla-carousel-autoplay";
import {cn} from "@/lib/utils";
import type {CarouselApi} from "@/components/ui/carousel";

/**
 * Embla plugins for a carousel that advances by itself every `delay` milliseconds. It pauses while
 * the pointer is over the carousel or focus is inside it, and it is left out altogether when the
 * visitor prefers reduced motion (WCAG 2.2.2), following a change of that preference live.
 */
export function useAutoplayPlugins(delay: number) {
    const [reduced, setReduced] = useState(true);
    useEffect(() => {
        const query = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(query.matches);
        update();
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
    }, []);
    return useMemo(
        () => (reduced ? [] : [Autoplay({delay, stopOnInteraction: false, stopOnMouseEnter: true})]),
        [reduced, delay],
    );
}

/** One dot per scroll position of an Embla carousel; the current one is marked with `aria-current`. */
export function CarouselDots({api, className}: {api: CarouselApi | undefined; className?: string}) {
    const [count, setCount] = useState(0);
    const [selected, setSelected] = useState(0);
    useEffect(() => {
        if (!api) return;
        const update = () => {
            setCount(api.scrollSnapList().length);
            setSelected(api.selectedScrollSnap());
        };
        update();
        api.on("reInit", update).on("select", update);
        return () => {
            api.off("reInit", update).off("select", update);
        };
    }, [api]);
    if (count < 2) return null;
    return (
        <div className={cn("flex flex-wrap justify-center gap-2", className)}>
            {Array.from({length: count}, (_, i) => (
                <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1} of ${count}`}
                    aria-current={i === selected ? "true" : undefined}
                    onClick={() => api?.scrollTo(i)}
                    className={cn(
                        "size-3 rounded-full border border-current transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
                        i === selected ? "bg-current" : "bg-transparent opacity-60 hover:opacity-100",
                    )}
                />
            ))}
        </div>
    );
}
